import { animate, style, transition, trigger } from '@angular/animations';
import { CdkContextMenuTrigger } from '@angular/cdk/menu';
import { OverlayModule } from '@angular/cdk/overlay';
import {
  booleanAttribute,
  Component,
  computed,
  DestroyRef,
  effect,
  HostListener,
  inject,
  input,
  model,
  OnDestroy,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { IPoint, PointExtensions } from '@foblex/2d';
import {
  EFConnectableSide,
  FCanvasComponent,
  FCreateConnectionEvent,
  FDragStartedEvent,
  FFlowComponent,
  FFlowModule,
  FSelectionChangeEvent,
  IFFlowStateNode,
} from '@foblex/flow';
import { generateGuid } from '@foblex/utils';
import { provideIcons } from '@ng-icons/core';
import {
  lucideCheck,
  lucideChevronDown,
  lucideCircleAlert,
  lucideCircleCheck,
  lucideCircleX,
  lucideContactRound,
  lucideCopy,
  lucideFileDown,
  lucideFlag,
  lucideGrid2x2,
  lucideGroup,
  lucideLayoutDashboard,
  lucideLoaderCircle,
  lucideNotebookText,
  lucideTicketCheck,
  lucideTrash,
  lucideUndo,
  lucideX,
} from '@ng-icons/lucide';
import { ChipComponent, ChipVariants } from '@shared/components/chip/chip.component';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { ToastrSuccessComponent } from '@shared/components/toastr/toastr-success.component';
import { LivrablePrincipal } from '@shared/models/livrable-principal.enum';
import { CanEditService } from '@shared/services/can-edit.service';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDialogService } from '@spartan-ng/helm/dialog';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { ClickDoubleDirective } from '@shared/directives/click-double.directive';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { UserStore } from '../auth/store/user.store';
import { Cartographie } from '../cartographie/models/cartographie.model';
import { RéférentielLivrableStore } from '../dashboard/store/référentiel-livrable.store';
import { InterrelationStore } from '../référentiel-métier/référentiel-metier-interrelations/store/interrelation.store';
import { DialogConfirmDeleteComponent } from '../shared/components/dialog/confirm-delete/dialog-confirm-delete.component';
import { ConfirmDialogComponent } from '../shared/components/dialog/confirm-dialog/confirm-dialog.component';
import { ButtonClosePanelComponent } from '../shared/components/sidepanel/button-close-panel/button-close-panel.component';
import { SidepanelService } from '../shared/components/sidepanel/sidepanel.service';
import { CELL_SIZE, TAILLE_JALON_DEFAULT, TAILLE_NOEUD_DEFAULT } from '../shared/models/noeud.constants';
import { CanvasGroup, CanvasRendererComponent } from './canvas-renderer/canvas-renderer.component';
import { BarreDOutilComponent, Outil } from './components/barre-d-outil/barre-d-outil.component';
import { ButtonEtapeClosePanelComponent } from './components/button-etape-close-panel.component';
import { ContextMenuComponent, MenuItem } from './components/context-menu/context-menu.component';
import { GroupeEtapesALaCarteComponent } from './components/groupe-etapes-a-la-carte/groupe-etapes-a-la-carte.component';
import { ImportEtapeFooterComponent } from './components/import-etape-footer/import-etape-footer.component';
import { ImportEtapeComponent } from './components/import-etape/import-etape.component';
import { FinParcours } from './models/fin-parcours.model';
import { GroupeÉtapesÀLaCarte } from './models/groupe-étapes-à-la-carte.model';
import { Jalon } from './models/jalon.model';
import { Module } from './models/module.model';
import { PointDepart } from './models/point-départ.model';
import { POINTS_DEPART } from './models/point.départ.enum';
import { Étape } from './models/étape.model';
import { ÉTAT_VALIDATION_ÉTAPE } from './models/état-validation-étape.enum';
import { ParcoursDetailComponent } from './parcours-detail/parcours-detail.component';
import { EtapeCreateRequest } from './parcours-etape/requests/create-request';
import { EtapeBodyRequest } from './parcours-etape/requests/update-request';
import { EtapeStore } from './parcours-etape/store/etape.store';
import { ModuleStore } from './parcours-module/store/module.store';
import { CreateGroupeÉtapesRequest } from './requests/create-groupe-étapes-request';
import { ParcoursStateService, ProfondeurActuelle } from './services/parcours-state.service';
import { FinParcoursStore } from './store/fin-parcours/fin-parcours.store';
import { GroupeÉtapesÀLaCarteStore } from './store/groupe-étapes-à-la-carte/groupe-étapes-à-la-carte.store';
import { JalonStore } from './store/jalon/jalon.store';
import { ParcoursStore } from './store/parcours.store';
import { Référentiel } from '../administration/gestion-référentiels/models/référentiel.model';

const ZOOM_FACTOR = 2;

type TYPE_ÉLÉMENT =
  | 'jalon'
  | 'étape'
  | 'groupe-étapes-à-la-carte'
  | 'module'
  | 'fin-parcours'
  | 'canvas'
  | 'connecteur'
  | null;

@Component({
  selector: 'app-referentiel-metier-parcours',
  imports: [
    FFlowModule,
    OverlayModule,
    RouterModule,
    CanvasRendererComponent,
    BarreDOutilComponent,
    HlmIconImports,
    CdkContextMenuTrigger,
    ContextMenuComponent,
    HlmButtonImports,
    GroupeEtapesALaCarteComponent,
    HlmTooltipImports,
    ChipComponent,
    HlmSpinnerImports,
    ClickDoubleDirective,
  ],
  templateUrl: './référentiel-métier-parcours.component.html',
  styleUrl: './référentiel-métier-parcours.component.scss',
  providers: [
    provideIcons({
      lucideContactRound,
      lucideGrid2x2,
      lucideTicketCheck,
      lucideFlag,
      lucideNotebookText,
      lucideLayoutDashboard,
      lucideTrash,
      lucideGroup,
      lucideCircleX,
      lucideCircleCheck,
      lucideChevronDown,
      lucideCircleAlert,
      lucideX,
      lucideCheck,
      lucideCopy,
      lucideUndo,
      lucideLoaderCircle,
      lucideFileDown,
    }),
  ],
  animations: [
    trigger('openClose', [
      transition(':enter', [
        style({ transform: 'translateX(120%)' }),
        animate('300ms ease-in', style({ transform: 'translateX(0)' })),
      ]),
      transition(':leave', [
        style({ transform: 'translateX(0)' }),
        animate('300ms ease-in', style({ transform: 'translateX(120%)' })),
      ]),
    ]),
  ],
})
export class RéférentielMétierParcoursComponent implements OnDestroy {
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #parcoursStore = inject(ParcoursStore);
  readonly #moduleStore = inject(ModuleStore);
  readonly #finParcoursStore = inject(FinParcoursStore);
  readonly #parcoursStateService = inject(ParcoursStateService);
  readonly #interrelationStore = inject(InterrelationStore);
  readonly #etapeStore = inject(EtapeStore);
  readonly #jalonStore = inject(JalonStore);
  readonly #groupeÉtapesStore = inject(GroupeÉtapesÀLaCarteStore);
  readonly #userStore = inject(UserStore);
  readonly #referentielLivrableStore = inject(RéférentielLivrableStore);
  readonly #dialog = inject(HlmDialogService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #canEditService = inject(CanEditService);

  public fFlow = viewChild.required(FFlowComponent);
  public fCanvas = viewChild.required(FCanvasComponent);
  protected readonly CELL_SIZE = CELL_SIZE;
  protected readonly TAILLE_NOEUD_DEFAULT = TAILLE_NOEUD_DEFAULT;
  protected readonly ETAT_VALIDATION_ETAPE = ÉTAT_VALIDATION_ÉTAPE;

  readonly cartographie = input.required<Cartographie>();
  readonly referentiel = input.required<Référentiel>();
  readonly roles = input.required<string[]>();

  readonly idParcours = input<string>();

  parcours = computed(() => this.#parcoursStore.selectedEntity());
  pointsDeDepart = computed(() => this.#etapeStore.pointsDeDepartActifs());
  jalons = computed(() => this.#parcoursStore.jalons());
  etapes = computed(() =>
    this.#etapeStore.étapesActives().map((e) => ({
      ...e,
      sommeMois:
        e.repartitionsAnnee
          ?.map((x) => x.nombreMois)
          .filter((x) => x !== null)
          .reduce((acc, element) => acc + element, 0) ?? 0,
    })),
  );

  etapesSupprimees = computed(() =>
    this.#etapeStore.étapesSupprimées().map((e) => ({
      ...e,
      sommeMois:
        e.repartitionsAnnee
          ?.map((x) => x.nombreMois)
          .filter((x) => x !== null)
          .reduce((acc, element) => acc + element, 0) ?? 0,
    })),
  );

  groupesEtapesALaCarte = computed(() => this.#groupeÉtapesStore.entities());
  finsParcours = computed(() => this.#parcoursStore.finsParcours());
  noeuds = computed(() => this.#etapeStore.entities());
  connecteurs = computed(() =>
    (this.#parcoursStore.connecteurs() ?? []).filter((x) => {
      const idPrécédent = (x.idEtapePrecedente ?? x.idGroupeEtapesALaCartePrecedent)!;
      const idSuivant = (x.idEtapeSuivante ?? x.idGroupeEtapesALaCarteSuivant)!;

      // On ne doit récupérer que les connecteurs qui sont liés à des étapes/groupes non supprimées
      return (
        (this.etapes()
          .map((x) => x.id)
          .includes(idPrécédent) ||
          this.groupesEtapesALaCarte()
            .map((x) => x.id)
            .includes(idPrécédent) ||
          this.pointsDeDepart()
            .map((x) => x.id)
            .includes(idPrécédent)) &&
        (this.etapes()
          .map((x) => x.id)
          .includes(idSuivant) ||
          this.groupesEtapesALaCarte()
            .map((x) => x.id)
            .includes(idSuivant) ||
          this.pointsDeDepart()
            .map((x) => x.id)
            .includes(idSuivant))
      );
    }),
  );

  etapeSelectionnee = computed(() => this.#etapeStore.selectedEntity());
  moduleSelectionne = computed(() => this.#moduleStore.selectedEntity());
  jalonSelectionne = computed(() => this.#jalonStore.selectedEntity());
  groupeEtapesSelectionne = computed(() => this.#groupeÉtapesStore.selectedEntity());
  finParcoursSelectionnee = computed(() => this.#finParcoursStore.selectedEntity());

  peutModifier = computed(() =>
    this.#canEditService.check(
      this.referentiel().equipeResponsable,
      this.roles(),
      LivrablePrincipal.PARCOURS_CROISEMENT,
      this.cartographie(),
      this.referentiel(),
    ),
  );

  estVerrouille = computed(
    () =>
      this.#referentielLivrableStore
        .entities()
        .find(
          (x) =>
            x.referentiel.id === this.referentiel()?.id &&
            x.nomLivrable === LivrablePrincipal.PARCOURS_CROISEMENT &&
            x.idParcours === this.parcours()?.id,
        )?.estVerrouille,
  );

  modulesMapByEtapeId = computed(() =>
    this.#moduleStore.entities().reduce(
      (acc, module) => {
        (acc[module.idEtape] ??= []).push(module);
        return acc;
      },
      {} as Record<string, Module[]>,
    ),
  );

  canvasEtape = computed<CanvasGroup[]>(() =>
    [...(this.etapes() ?? [])].map((e) => ({
      ...e,
      position: { x: e.metadonnees.x * ZOOM_FACTOR, y: e.metadonnees.y * (ZOOM_FACTOR + 1) }, // +1 car le point est en haut à gauche du noeud
      size: { width: e.metadonnees.width, height: e.metadonnees.height },
      modules: this.modulesMapByEtapeId()[e.id],
    })),
  );

  idJalonSelectionné = signal('');
  #idGroupeÉtapesSelectionné = signal('');
  #idsConnecteursSélectionnés = signal([] as string[]);
  #idsEtapesSelectionnees = signal(['']);
  idModuleSelectionne = signal('');
  estDetailOuvert = input<boolean, string>(false, {
    alias: 'detail',
    transform: booleanAttribute,
  });

  #dragType: string = '';

  protected readonly profondeurActuelle = this.#parcoursStateService.profondeurActuelle;

  typeElementSelectionne = input<TYPE_ÉLÉMENT>(null, { alias: 'type' });
  idElementSelectionne = model<string | null>(null, { alias: 'id' });
  niveauActuel = input<ProfondeurActuelle>('01', { alias: 'niveau' });
  canvasSupprime = input<boolean>(false, { alias: 'canvasSupprime' });

  showErrors = input<boolean>(false);
  estCanvasElementsSupprimes = signal<boolean>(this.canvasSupprime());

  #creationCoord: IPoint = { x: 0, y: 0 };

  isDialogOpen = signal(false);
  isLoading = signal<boolean>(false);

  ngOnDestroy(): void {
    toast.dismiss();
    this.#sidepanelService.close();
  }

  protected nodeContextMenuItems(
    data: Jalon | Étape | GroupeÉtapesÀLaCarte | Module | FinParcours,
    type: TYPE_ÉLÉMENT,
  ) {
    let result: MenuItem<Étape | Jalon | FinParcours>[] = [];

    if (type !== 'canvas' && type !== 'connecteur') {
      result.push({
        label: 'Afficher les détails',
        icon: 'lucideNotebookText',
        action: (node: Étape | Jalon | FinParcours | undefined) => {
          if (!node) {
            console.warn('Step/milestone/endpoint is undefined, cannot show details.');
            return;
          }
          this.#router.navigate([], { queryParams: { detail: true }, queryParamsHandling: 'merge' });
        },
      });
    }
    if (this.peutModifier()) {
      if (this.#idsEtapesSelectionnees().length > 1) {
        result = [];
        const etapes = this.#etapeStore.entities().filter((x) => this.#idsEtapesSelectionnees().includes(x.id));

        if (
          this.#userStore.isAdmin() &&
          etapes.filter((x) => x.estExportee).length === this.#idsEtapesSelectionnees().length
        ) {
          result.push({
            label: 'Déverrouiller les étapes',
            icon: 'lucideX',
            action: (etape) => {
              if (!etape) return;
              this.#déverrouillerEtape(etapes as Étape[]);
            },
          });
        }
      } else if (type === 'étape') {
        const step = data as Étape;
        const etape = this.#etapeStore.entityMap()[step.id];

        if (this.estCanvasElementsSupprimes()) {
          result.push({
            label: "Réactiver l'étape",
            icon: 'lucideUndo',
            action: (etape) => {
              if (!etape) return;
              this.toggleSuppressionEtape(etape.id);
            },
          });
        } else {
          const labelToggleOuverture = `${etape.estOuverte ? 'Fermer' : 'Rouvrir'} l'étape au recrutement`;
          const iconeOuverture = etape.estOuverte ? 'lucideCircleX' : 'lucideCircleCheck';

          const labelToggleValidation = `${etape.etatValidation === ÉTAT_VALIDATION_ÉTAPE.VALIDEE ? 'Dévalider' : 'Valider'} l'étape`;
          const iconeValidation = etape.etatValidation === ÉTAT_VALIDATION_ÉTAPE.VALIDEE ? 'lucideX' : 'lucideCheck';

          const labelToggleDemandeValidation = `${etape.etatValidation === ÉTAT_VALIDATION_ÉTAPE.EN_COURS ? 'Demander' : 'Annuler'} la validation`;
          const iconeDemandeValidation =
            etape.etatValidation === ÉTAT_VALIDATION_ÉTAPE.EN_COURS ? 'lucideCheck' : 'lucideX';

          result.push(
            {
              label: "Modulariser l'étape",
              icon: 'lucideLayoutDashboard',
              action: (etape) => {
                if (!etape) return;
                this.#router.navigate([], {
                  queryParams: { detail: false, niveau: '02' },
                  queryParamsHandling: 'merge',
                });
              },
            },
            {
              label: labelToggleOuverture,
              icon: iconeOuverture,
              action: (etape) => {
                if (!etape) return;
                this.#fermerEtape(etape as Étape);
              },
            },
            {
              label: 'Dupliquer',
              icon: 'lucideCopy',
              action: (etape) => {
                if (!etape) return;
                this.#dupliquerEtape(etape as Étape);
              },
            },
          );

          if (this.#userStore.isAdmin()) {
            result.push({
              label: 'Télécharger cette étape',
              icon: 'lucideFileDown',
              action: (etape) => {
                if (!etape) return;
                this.#téléchargerEtape(etape as Étape);
              },
            });
          }

          if (this.#userStore.isAdmin() && !etape.estExportee) {
            result.push({
              label: labelToggleValidation,
              icon: iconeValidation,
              action: (etape) => {
                if (!etape) return;
                this.#toggleValidation(etape as Étape);
              },
            });
          } else if (this.#userStore.isAdmin() && etape.estExportee) {
            result.push({
              label: "Déverrouiller l'étape",
              icon: 'lucideX',
              action: (etape) => {
                if (!etape) return;
                this.#déverrouillerEtape([etape] as Étape[]);
              },
            });
          }

          if (this.#userStore.canAskValidationEtape() && !etape.estExportee) {
            result.push({
              label: labelToggleDemandeValidation,
              icon: iconeDemandeValidation,
              action: (etape) => {
                if (!etape) return;
                this.#toggleDemandeValidation(etape as Étape);
              },
            });
          }

          if (etape.estPointDepart) result = [];
          if (!etape.estExportee) {
            result.push({
              label: 'Supprimer',
              icon: 'lucideTrash',
              action: (etape) => {
                if (!etape) return;
                this.toggleSuppressionEtape(etape.id);
              },
            });
          }
        }
      } else if (type === 'canvas' && !this.estCanvasElementsSupprimes()) {
        //// result.push({
        ////   label: 'Créer un palier',
        ////   icon: 'lucideFlag',
        ////   action: () => this.ajouterJalon(true),
        //// });

        if (this.#userStore.isAdmin()) {
          result.push({
            label: 'Créer une étape',
            icon: 'lucideGrid2x2',
            children: [
              {
                label: 'Créer une nouvelle étape',
                action: () => this.ajouterEtape(true),
              },
              {
                label: 'Récupérer une étape',
                action: () => this.#récupérerÉtape(true),
              },
            ],
          });
        } else {
          result.push({
            label: 'Créer une étape',
            icon: 'lucideGrid2x2',
            action: () => this.ajouterEtape(true),
          });
        }
        result.push({
          label: "Créer un groupe d'étapes à la carte",
          icon: 'lucideGroup',
          action: () => this.ajouterGroupeEtape(true),
        });

        if (Object.values(POINTS_DEPART).filter((p) => !this.pointsDeDepart().find((x) => x.nom == p)).length > 0) {
          result.push({
            label: 'Créer un point de départ',
            icon: 'lucideTicketCheck',
            children: Object.values(POINTS_DEPART)
              .filter((p) => !this.pointsDeDepart().find((x) => x.nom == p))
              .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
              .map<MenuItem<Étape | Jalon | FinParcours | undefined>>((pointDépart) => ({
                label: pointDépart,
                type: 'item',
                action: () => this.ajoutPointDepart(pointDépart, true),
              })),
          });
        }
      } else if (type === 'jalon') {
        result.push({
          label: 'Supprimer',
          icon: 'lucideTrash',
          action: (jalon: Étape | Jalon | FinParcours | undefined) => {
            if (!jalon) return;
            this.#supprimerJalon(jalon.id);
          },
        });
      } else if (type === 'fin-parcours') {
        result.push({
          label: 'Supprimer',
          icon: 'lucideTrash',
          action: (finParcours: Étape | Jalon | FinParcours | undefined) => {
            if (!finParcours) return;
            this.#supprimerFinParcours(finParcours.id);
          },
        });
      } else if (type === 'connecteur') {
        result.push({
          label: 'Supprimer',
          icon: 'lucideTrash',
          action: () => this.#supprimerConnecteurs(),
        });
      }
    }

    return result;
  }

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (this.isDialogOpen() || this.estDetailOuvert() || this.#sidepanelService.isOpen()) return;
    if (event.key === 'Backspace' || event.key === 'Delete') {
      this.#suppression();
    }
  }

  constructor() {
    effect(() => this.#interrelationStore.chargerToutes(this.cartographie().id));

    effect(() => this.#parcoursStateService.setProfondeurActuelle(this.niveauActuel() ?? '01'));

    effect(() => {
      const idParcours = this.idParcours();
      const parcours = this.#parcoursStore.entities();
      const niveauActuel = this.niveauActuel();

      if (!idParcours) {
        this.#router.navigate([parcours[0].id], {
          relativeTo: this.#route,
          queryParams: { niveau: niveauActuel },
        });
      }
    });

    effect(() => {
      const niveau = this.niveauActuel();
      if (niveau === '01') {
        this.#finParcoursStore.charger(this.referentiel().id);
      }
    });

    effect(() => {
      let elementNom = '';
      const type = this.typeElementSelectionne();

      if (type) {
        switch (type) {
          case 'étape':
            const etape = untracked(() => this.etapeSelectionnee());
            if (etape) {
              elementNom = etape.nom!;
            }
            break;
          case 'module':
            const module = untracked(() => this.moduleSelectionne());
            if (module) {
              elementNom = module.nom;
            }
            break;
          case 'jalon':
            const jalon = untracked(() => this.jalonSelectionne());
            if (jalon) {
              elementNom = jalon.nom!;
            }
            break;
          case 'fin-parcours':
            const finParcours = untracked(() => this.finParcoursSelectionnee());
            if (finParcours) {
              elementNom = finParcours.nom;
            }
            break;
          case 'groupe-étapes-à-la-carte':
            const groupe = untracked(() => this.groupeEtapesSelectionne());
            if (groupe) {
              elementNom = groupe.nom;
            }
            break;
        }

        if (this.estDetailOuvert()) {
          this.#sidepanelService.setComponentToDisplay(
            elementNom,
            ParcoursDetailComponent,
            undefined,
            {
              referentiel: this.referentiel(),
              type: type,
              cartographie: this.cartographie(),
              peutModifier: this.peutModifier(),
              showErrors: this.showErrors(),
            },
            type === 'étape' ? ButtonEtapeClosePanelComponent : ButtonClosePanelComponent,
          );
          this.#sidepanelService.open();
        }
      }
    });

    effect(() => {
      const etape = this.etapeSelectionnee();

      const chips: { label: string; variant: ChipVariants['variant'] }[] = [];
      if (etape) {
        if (etape.etatValidation === ÉTAT_VALIDATION_ÉTAPE.VALIDEE) {
          chips.push({
            label: 'Validée',
            variant: 'positive',
          });
        }

        if (this.estVerrouille()) {
          chips.push({
            label: 'Verrouillée',
            variant: 'warning',
          });
        }

        if (!etape.estOuverte) {
          chips.push({
            label: 'Fermée',
            variant: 'default',
          });
        }

        if (etape.estExportee) {
          chips.push({
            label: 'Exportée',
            variant: 'informative',
          });
        }
      }
      this.#sidepanelService.chips.set(chips);
    });
  }

  etapeSansPredecesseur = (idÉtape: string) => !this.connecteurs()?.some((c) => c.idEtapeSuivante === idÉtape);

  ajoutPointDepart(pointDepart: string, fromCoord?: boolean) {
    const parcours = this.parcours();
    if (!parcours) return;

    const pointsDeDépart = this.pointsDeDepart() ?? [];
    const étapes = this.etapes() ?? [];
    const jalons = this.jalons() ?? [];
    let { x, y } = { x: 0, y: 0 };

    if (fromCoord) {
      x = this.#creationCoord.x;
      y = this.#creationCoord.y;
    } else if (pointsDeDépart.length !== 0) {
      x = Math.max(...pointsDeDépart.map((pdd) => pdd.metadonnees.x));
      y = Math.round(Math.max(...pointsDeDépart.map((x) => x.metadonnees.y))) + CELL_SIZE;
    } else if (étapes.length !== 0) {
      x = Math.min(...étapes.map((e) => e.metadonnees.x)) - CELL_SIZE;
      y = Math.min(...étapes.map((e) => e.metadonnees.y));
    } else if (jalons.length !== 0) {
      x = Math.min(...jalons.map((x) => x.metadonnees.x)) - CELL_SIZE * 2;
      y = Math.min(...jalons.map((x) => x.metadonnees.y)) + CELL_SIZE;
    }
    this.#etapeStore.créer({
      request: {
        idParcours: parcours.id,
        idReferentiel: this.referentiel().id,
        nom: pointDepart,
        metadonnees: {
          x: Math.floor(x),
          y: Math.floor(y),
          width: TAILLE_NOEUD_DEFAULT.width,
          height: TAILLE_NOEUD_DEFAULT.height,
        },
        estPointDepart: true,
      },
      next: () => {},
    });
  }

  ajouterJalon(fromCoord?: boolean) {
    const nouvelId = generateGuid().replaceAll('-', '');
    const { maxX, yUltimateJalon } = this.jalons().reduce(
      (acc, currJalon) => {
        if (currJalon.metadonnees.x > acc.maxX) {
          acc.maxX = currJalon.metadonnees.x + (CELL_SIZE * 5 - TAILLE_JALON_DEFAULT.width);
          acc.yUltimateJalon = currJalon.metadonnees.y;
        }
        return acc;
      },
      { maxX: 0, yUltimateJalon: 0 },
    );

    this.#parcoursStore.ajouterJalon({
      id: nouvelId,
      nom: null,
      metadonnees: {
        x: fromCoord ? Math.floor(this.#creationCoord.x) : maxX + TAILLE_JALON_DEFAULT.width + CELL_SIZE,
        y: yUltimateJalon,
        width: TAILLE_JALON_DEFAULT.width,
        height: TAILLE_JALON_DEFAULT.height,
      },
    });
    this.#parcoursStore.sauvegarderParcours();
    this.fCanvas().centerGroupOrNode(nouvelId);
  }

  /**
   * Cette méthode permet d'affecter une nouvelle étape.
   * Si une étape est sélectionnée sur le canva, la nouvelle étape sera liée à l'étape sélectionnée.
   */
  ajouterEtape(fromCoord?: boolean): void {
    const parcours = this.parcours();
    if (!parcours) return;

    const idÉtapeSélectionnée = this.#idsEtapesSelectionnees()[0];
    let { x, y } = fromCoord ? this.#creationCoord : { x: 0, y: 0 };
    if (idÉtapeSélectionnée) {
      const étapePrécédente = this.noeuds()?.find((e) => e.id === idÉtapeSélectionnée);
      if (!étapePrécédente) return;

      x = étapePrécédente.metadonnees.x + CELL_SIZE * 2;
      y = étapePrécédente.metadonnees.y;
    }

    const nouvelleÉtape: EtapeCreateRequest = {
      idParcours: parcours.id,
      idReferentiel: this.referentiel().id,
      metadonnees: {
        x: Math.floor(x),
        y: Math.floor(y),
        width: TAILLE_NOEUD_DEFAULT.width,
        height: TAILLE_NOEUD_DEFAULT.height,
      },
      estPointDepart: false,
    };

    this.#etapeStore.créer({
      request: nouvelleÉtape,
      next: (idNouvelleÉtape) => {
        if (idÉtapeSélectionnée) {
          this.#parcoursStore.ajouterConnecteur({
            idEtapePrecedente: idÉtapeSélectionnée,
            idEtapeSuivante: idNouvelleÉtape,
          });
        }

        this.#parcoursStore.sauvegarderParcours();
        this.fCanvas().centerGroupOrNode(idNouvelleÉtape);
      },
    });
  }

  /**
   * Cette méthode permet de créer un nouveau groupe d'étapes.
   * Si une étape est sélectionnée sur le canva, la nouvelle étape sera liée à l'étape sélectionnée.
   */
  ajouterGroupeEtape(fromCoord?: boolean): void {
    const parcours = this.parcours();
    if (!parcours) return;

    const idÉtapeSélectionnée = this.#idsEtapesSelectionnees()[0];

    let noeuds = computed(() => this.noeuds());
    let { x, y } = fromCoord ? this.#creationCoord : this.fCanvas().getPosition();

    if (idÉtapeSélectionnée) {
      const étapePrécédente = noeuds()?.find((e) => e.id === idÉtapeSélectionnée);
      if (!étapePrécédente) return;

      x = étapePrécédente.metadonnees.x + CELL_SIZE * 2;
      y = étapePrécédente.metadonnees.y;
    }

    const nouveauGroupeÉtape: CreateGroupeÉtapesRequest = {
      idParcours: parcours.id,
      idReferentiel: this.referentiel().id,
      nom: 'Formation à la carte ' + this.#groupeÉtapesStore.entities().length,
      metadonnees: {
        x: Math.floor(x),
        y: Math.floor(y),
        width: TAILLE_NOEUD_DEFAULT.width,
        height: TAILLE_NOEUD_DEFAULT.height,
      },
    };

    this.#groupeÉtapesStore.créer(nouveauGroupeÉtape);
  }

  #téléchargerEtape(etape: Étape) {
    this.isLoading.set(true);
    this.#etapeStore.download({ id: etape.id, next: (blob: Blob) => this.#downloadJson(blob) });
  }

  #downloadJson(blob: Blob) {
    const etape = this.#etapeStore.selectedEntity();
    if (!etape) return;

    const filename = `export-${etape.nom}.json`;

    const downloadLink = document.createElement('a');
    const url = URL.createObjectURL(new Blob([blob], { type: 'text/json;charset=utf-8' }));
    downloadLink.href = url;
    downloadLink.download = filename;

    document.body.appendChild(downloadLink);
    downloadLink.click();
    downloadLink.remove();
    URL.revokeObjectURL(url);
    this.isLoading.set(false);
  }

  #findUltimateModuleIndex(idEtape: string): number {
    const modules = this.modulesMapByEtapeId()[idEtape];
    if (!modules || modules.length === 0) return 0;

    return Math.max(...modules.map((m) => m.indice));
  }

  #ajouterModule() {
    const idEtapeSelectionnée = this.#idsEtapesSelectionnees()[0];

    if (!idEtapeSelectionnée) {
      console.error('Aucune étape sélectionnée pour ajouter un module.');
      return;
    }
    const indiceModule = this.#findUltimateModuleIndex(idEtapeSelectionnée) + 1;

    const id = generateGuid().replaceAll('-', '');
    this.#moduleStore.ajouterUnModule({
      id,
      nom: `Module ${indiceModule.toString().padStart(2, '0')} - intitulé`,
      idEtape: idEtapeSelectionnée,
      indice: indiceModule,
    });

    this.#parcoursStore.sauvegarderParcours();
  }

  protected handleCreateNode(event: Omit<Outil, 'icon'>) {
    switch (event.nodeType) {
      case 'Jalon':
        this.ajouterJalon();
        break;
      case 'Etape':
        this.ajouterEtape();
        break;
      case 'Point-Depart':
        this.ajoutPointDepart((event as PointDepart).libelleSchema);
        break;
      case 'Module':
        this.#ajouterModule();
        break;
      default:
        console.error(`Node type not handle: ${event.nodeType}`);
        break;
    }
  }

  protected handleCreateFinParcours(finParcours: FinParcours) {
    const jalons = this.jalons() ?? [];
    const finsParcours = this.finsParcours() ?? [];
    let x = 0;
    let y = 0;

    if (!finsParcours.map((x) => x.id).includes(finParcours.id)) {
      if (finsParcours.length > 0) {
        x = Math.max(...finsParcours.map((f) => f.metadonnees?.x ?? 0));
        y = Math.round(Math.max(...finsParcours.map((x) => x.metadonnees?.y ?? 0))) + CELL_SIZE;
      } else if (jalons.length !== 0) {
        x = Math.max(...jalons.map((x) => x.metadonnees.x + x.metadonnees.width)) + CELL_SIZE;
        y = Math.min(...jalons.map((x) => x.metadonnees.y)) + CELL_SIZE;
      }

      this.#parcoursStore.ajouterFinParcours({
        ...finParcours,
        metadonnees: {
          x: Math.round(x),
          y: y,
          width: TAILLE_NOEUD_DEFAULT.width,
          height: TAILLE_NOEUD_DEFAULT.height,
        },
      });
      this.#parcoursStore.sauvegarderParcours();
    }
    this.fCanvas().centerGroupOrNode(finParcours.id.replaceAll('-', ''));
  }

  navigateWithPathParameters(detail?: boolean, type?: TYPE_ÉLÉMENT, showErrors?: boolean) {
    this.#router.navigate([], {
      relativeTo: this.#route,
      queryParams: {
        id: this.idElementSelectionne(),
        niveau: this.profondeurActuelle(),
        type: type !== undefined ? type : this.typeElementSelectionne(),
        detail: detail ?? this.#sidepanelService.isOpen(),
        showErrors: showErrors,
        canvasSupprime: this.estCanvasElementsSupprimes(),
      },
      queryParamsHandling: 'merge',
    });
  }

  handleModuleOrderChange(module: Module) {
    this.#moduleStore.enregistrerOrdre({
      id: module.id,
      request: {
        nom: module.nom,
        indice: module.indice,
      },
    });
  }

  protected handleGroupeSelection(event: (string | null)[]) {
    this.#idsEtapesSelectionnees.set([event[0]!]);

    this.idElementSelectionne.set(this.#idsEtapesSelectionnees()[0]);
    this.navigateWithPathParameters(this.#sidepanelService.isOpen(), 'étape');
  }

  protected handleNodeSelection(event: (string | null)[]) {
    this.idElementSelectionne.set(event[0]);
    this.navigateWithPathParameters(this.#sidepanelService.isOpen(), 'module');
  }

  public onSelectionChange(event: FSelectionChangeEvent): void {
    if (event.groupIds) {
      this.idJalonSelectionné.set(event.groupIds[0]);
    }

    this.#idsEtapesSelectionnees.set(event.nodeIds);

    this.#idsConnecteursSélectionnés.set(event.connectionIds);

    let type = this.typeElementSelectionne();

    if (event.nodeIds.length === 1) {
      const idEtapeSelectionnee = event.nodeIds[0];
      const estUnPointDeDepart =
        this.pointsDeDepart()
          ?.map((x) => x.id)
          .includes(idEtapeSelectionnee) ?? false;

      const estUneFinDeParcours =
        this.finsParcours()
          ?.map((x) => x.id)
          .includes(idEtapeSelectionnee) ?? false;

      if (this.#idsEtapesSelectionnees()) {
        if (estUneFinDeParcours) {
          type = 'fin-parcours';
        } else {
          if (
            this.noeuds()?.find((x) => x.id === idEtapeSelectionnee) ||
            this.groupesEtapesALaCarte()
              .flatMap((x) => x.etapes)
              .map((x) => x.id)
              .includes(idEtapeSelectionnee)
          ) {
            type = 'étape';
          } else {
            type = 'groupe-étapes-à-la-carte';
          }
        }
        this.idElementSelectionne.set(idEtapeSelectionnee);
      }

      if (this.idJalonSelectionné()) {
        type = 'jalon';
        this.idElementSelectionne.set(this.idJalonSelectionné());
      }

      if (estUnPointDeDepart) {
        type = null;
      }

      if (this.estDetailOuvert() && estUnPointDeDepart) {
        type = null;
        this.idElementSelectionne.set(null);
        this.fermerDetails();
        return;
      }
    } else {
      type = null;
    }

    this.navigateWithPathParameters(this.#sidepanelService.isOpen(), type);
    this.fFlow().redraw();
  }

  onLoaded(): void {
    this.#fitToScreen();
  }

  #fitToScreen(): void {
    this.fCanvas().fitToScreen(PointExtensions.initialize(550, 50), true);
  }

  deplacementElementFini(): void {
    if (!this.peutModifier()) return;

    if (this.#dragType !== 'move-node') return;
    const flowState = this.fFlow().getState();

    if (this.typeElementSelectionne() === 'groupe-étapes-à-la-carte') {
      this.#déplacerGroupeÉtapes();
    } else {
      this.#déplacerÉtapes();
    }

    const nodes: IFFlowStateNode[] = flowState.nodes;

    const anciennesFins = this.finsParcours() ?? [];

    const nouvellesFins: FinParcours[] = [];
    const nouveauxJalons: Jalon[] = [];

    nodes.forEach((n) => {
      if (n.fOutputs.filter((o) => o.fConnectableSide === EFConnectableSide.RIGHT).length === 0) {
        const fin = anciennesFins.find((e) => e.id === n.id)!;

        if (!fin) {
          console.error(n);
          return;
        }

        nouvellesFins.push({
          id: n.id,
          nom: fin.nom,
          metadonnees: {
            ...fin.metadonnees!,
            x: Math.round(n.position.x),
            y: Math.round(n.position.y),
          },
          description: fin.description!,
        });
      }
    });

    this.#parcoursStore.attribuerJalons(nouveauxJalons);
    this.#parcoursStore.attribuerFinsParcours(nouvellesFins);
    this.#parcoursStore.sauvegarderParcours();
  }

  dragCommence(event: FDragStartedEvent) {
    this.#dragType = event.fEventType;
  }

  onConnectionDropped(event: FCreateConnectionEvent): void {
    if (event.targetId) {
      this.#créerConnexion(event.sourceId, event.targetId);
    }
  }

  ouvreDetails() {
    if (!this.typeElementSelectionne() && !this.idElementSelectionne()) return;

    this.navigateWithPathParameters(true);
  }

  fermerDetails() {
    this.navigateWithPathParameters(false);
  }

  #getEtapeById(id: string): Étape | undefined {
    return this.noeuds()?.find((e) => e.id === id);
  }

  #getJalonById(id: string): Jalon | undefined {
    return this.jalons()?.find((e) => e.id === id);
  }

  centrerEtapeALaCarte(idEtape: string) {
    this.fCanvas().centerGroupOrNode(idEtape);
    this.fCanvas().setScale(1);
  }

  /**
   * Permet de créer une connexion entre deux noeuds.
   * Si la connexion se fait à partir d'une étape d'un groupe ou jusque l'étape d'un groupe,
   * il faut que les deux étapes soient dans le groupe.
   * @param idSource Identifiant de la source : `output--id--[l/r]`
   * @param idCible Identifiant de la cible : `input--id`
   */
  #créerConnexion(idSource: string, idCible: string) {
    if (!this.peutModifier()) return;

    const source = idSource.split('--');
    const target = idCible.split('--');

    let idÉtapePrécédente = source.at(1);
    let idÉtapeSuivante = target.at(1);

    if (idSource.split('--').at(-1) === 'l') {
      const temp = idÉtapeSuivante;
      idÉtapeSuivante = idÉtapePrécédente;
      idÉtapePrécédente = temp;
    }

    if (idÉtapePrécédente !== idÉtapeSuivante) {
      this.#parcoursStore.ajouterConnecteur({
        idEtapePrecedente: this.noeuds()?.find((x) => x.id === idÉtapePrécédente) ? idÉtapePrécédente : undefined,
        idGroupeEtapesALaCartePrecedent: this.groupesEtapesALaCarte()?.find((x) => x.id === idÉtapePrécédente)
          ? idÉtapePrécédente
          : undefined,
        idEtapeSuivante: this.noeuds()?.find((x) => x.id === idÉtapeSuivante) ? idÉtapeSuivante : undefined,
        idGroupeEtapesALaCarteSuivant: this.groupesEtapesALaCarte()?.find((x) => x.id === idÉtapeSuivante)
          ? idÉtapeSuivante
          : undefined,
      });
      this.#parcoursStore.sauvegarderParcours();
    }
  }

  #suppression() {
    if (!this.peutModifier()) return;

    if (this.#idsConnecteursSélectionnés()) {
      this.#supprimerConnecteurs();
    }

    switch (this.typeElementSelectionne()) {
      case 'étape':
        this.toggleSuppressionEtape(this.#idsEtapesSelectionnees()[0]);
        break;
      case 'groupe-étapes-à-la-carte':
        this.supprimerGroupe(this.#idGroupeÉtapesSelectionné());
        break;
      case 'jalon':
        this.#supprimerJalon(this.idJalonSelectionné());
        break;
      case 'module':
        this.supprimerModule(this.idElementSelectionne());
        break;
      case 'fin-parcours':
        this.#supprimerFinParcours(this.idElementSelectionne());
    }
  }

  #supprimerConnecteurs() {
    const idsConnecteurs = this.#idsConnecteursSélectionnés().map((x) => x.split('-', 2));

    for (const [idÉtapePrécédente, idÉtapeSuivante] of idsConnecteurs) {
      this.#parcoursStore.supprimerConnecteur({
        idEtapePrecedente: this.noeuds()?.find((x) => x.id === idÉtapePrécédente) ? idÉtapePrécédente : undefined,
        idGroupeEtapesALaCartePrecedent: this.groupesEtapesALaCarte()?.find((x) => x.id === idÉtapePrécédente)
          ? idÉtapePrécédente
          : undefined,
        idEtapeSuivante: this.noeuds()?.find((x) => x.id === idÉtapeSuivante) ? idÉtapeSuivante : undefined,
        idGroupeEtapesALaCarteSuivant: this.groupesEtapesALaCarte()?.find((x) => x.id === idÉtapeSuivante)
          ? idÉtapeSuivante
          : undefined,
      });
    }
    this.#parcoursStore.sauvegarderParcours();
    this.#idsConnecteursSélectionnés.set([]);
  }

  toggleSuppressionEtape(idEtape: string) {
    const step = this.#getEtapeById(idEtape);
    if (!step) return;

    const nbModules = step.modules.length;

    let dialogRef;

    this.isDialogOpen.set(true);
    if (nbModules) {
      dialogRef = this.#dialog.open(DialogConfirmDeleteComponent, {
        context: {
          nom: step.nom,
          childrenText: `${nbModules} modules liés`,
          typeElement: "l'étape",
        },
      });
    } else {
      dialogRef = this.#dialog.open(ConfirmDialogComponent, {
        context: {
          titre: 'Attention',
          contenu: `Êtes-vous sûr de vouloir ${this.estCanvasElementsSupprimes() ? 'réactiver' : 'supprimer'} l\'étape ${step.nom} ?`,
        },
      });
    }

    dialogRef.closed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((result) => {
      if (result) {
        this.#etapeStore.toggleSuppression({
          id: idEtape,
          next: () => {
            this.#parcoursStore.chargerConnecteurs(
              this.#parcoursStore
                .selectedEntity()
                ?.connecteurs.filter((x) => x.idEtapePrecedente !== idEtape && x.idEtapeSuivante !== idEtape) ?? [],
            );
            this.#etapeStore.charger(this.idParcours()!);
            this.isDialogOpen.set(false);
          },
        });
        this.idElementSelectionne.set(null);
        this.navigateWithPathParameters();
      }
    });
  }

  #dupliquerEtape(étapeSource: Étape) {
    const parcours = this.parcours();
    if (!parcours) return;

    const idÉtapeSélectionnée = this.#idsEtapesSelectionnees()[0];
    let { x, y } = { x: 0, y: 0 };
    if (idÉtapeSélectionnée) {
      x = étapeSource.metadonnees.x + CELL_SIZE * 2;
      y = étapeSource.metadonnees.y;
    }

    const nouvelleÉtape: EtapeCreateRequest = {
      idParcours: parcours.id,
      idReferentiel: this.referentiel().id,
      metadonnees: {
        x: Math.floor(x),
        y: Math.floor(y),
        width: TAILLE_NOEUD_DEFAULT.width,
        height: TAILLE_NOEUD_DEFAULT.height,
      },
      estPointDepart: false,
      idEtapeSource: étapeSource.id,
      nom: étapeSource.nom + ' Copie',
    };
    this.isLoading.set(true);
    this.#etapeStore.créer({
      request: nouvelleÉtape,
      next: (idNouvelleÉtape) => {
        this.#etapeStore.getEtape(étapeSource.id);
        this.#parcoursStore.sauvegarderParcours();
        this.fCanvas().centerGroupOrNode(idNouvelleÉtape);
        setTimeout(() => this.isLoading.set(false), 400);
      },
    });
  }

  #supprimerFinParcours(idFinParcours: string | null) {
    if (!idFinParcours) return;

    const step = this.#parcoursStore.finsParcours()?.find((x) => x.id === idFinParcours);
    if (!step) return;

    this.isDialogOpen.set(true);
    const dialogRef = this.#dialog.open(ConfirmDialogComponent, {
      context: {
        titre: 'Attention',
        contenu: `Êtes-vous sûr de vouloir supprimer la fin de parcours ${step.nom} ?`,
      },
    });

    dialogRef.closed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((result) => {
      this.isDialogOpen.set(false);
      if (result) {
        this.#parcoursStore.supprimerFinParcours(idFinParcours);
        this.#parcoursStore.sauvegarderParcours();
        this.idElementSelectionne.set(null);
        this.navigateWithPathParameters(false, null);
      }
    });
  }

  supprimerModule(idModule: string | null) {
    if (!idModule) return;

    const module = this.#moduleStore.entityMap()[idModule];
    const etape = this.etapes()?.find((x) => x.id === module.idEtape);

    this.isDialogOpen.set(true);
    const dialogRef = this.#dialog.open(DialogConfirmDeleteComponent, {
      context: {
        item: module,
        parentText: `Lié à ${etape?.nom}`,
        childrenText: `${module.sequences.length} séquences liées`,
        description: `Si vous validez la suppression du module ${module.nom}, ses séquences seront également supprimées.`,
        typeElement: 'séquence',
      },
    });

    dialogRef.closed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((result) => {
      this.isDialogOpen.set(false);
      if (result) {
        this.#moduleStore.supprimer(idModule);
        this.idElementSelectionne.set(null);
        this.navigateWithPathParameters();
      }
    });
  }

  #supprimerJalon(idJalon: string) {
    const milestone = this.#getJalonById(idJalon);
    if (!milestone) return;

    this.isDialogOpen.set(true);
    const dialogRef = this.#dialog.open(ConfirmDialogComponent, {
      context: {
        titre: 'Attention',
        contenu: `Êtes-vous sûr de vouloir supprimer le jalon ${milestone.nom} ?`,
      },
    });

    dialogRef.closed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((result) => {
      this.isDialogOpen.set(false);
      if (result) {
        this.#parcoursStore.supprimerJalon(idJalon);
        this.#parcoursStore.sauvegarderParcours();
        this.idElementSelectionne.set(null);
        this.navigateWithPathParameters();
      }
    });
  }

  supprimerGroupe(idGroupe: string) {
    const groupe = this.#groupeÉtapesStore.entityMap()[idGroupe];

    const dialogRef = this.#dialog.open(ConfirmDialogComponent, {
      context: {
        titre: 'Attention',
        contenu: `Êtes-vous sûr de vouloir supprimer ${groupe.nom} ?`,
      },
    });

    dialogRef.closed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((result) => {
      if (result) {
        this.#groupeÉtapesStore.supprimer({
          id: groupe.id,
          next: () => {
            this.#parcoursStore.chargerConnecteurs(
              this.#parcoursStore
                .selectedEntity()
                ?.connecteurs.filter(
                  (x) =>
                    x.idGroupeEtapesALaCartePrecedent !== groupe.id && x.idGroupeEtapesALaCarteSuivant !== groupe.id,
                ) ?? [],
            );

            this.idElementSelectionne.set(null);
            this.navigateWithPathParameters();
          },
        });
      }
    });
  }

  #toggleValidation(data: Jalon | Étape | FinParcours) {
    const etape = this.#etapeStore.entityMap()[data.id];

    if (!etape) return;

    if (!this.handleValidationEtape(etape)) return;

    const toastrId = uuidv7();

    const { nom, codeEtape, duree, typesContrat, repartitionsAnnee, type, etatValidation } = etape;

    const étatValidationÉtape =
      etatValidation === ÉTAT_VALIDATION_ÉTAPE.VALIDEE ? ÉTAT_VALIDATION_ÉTAPE.EN_COURS : ÉTAT_VALIDATION_ÉTAPE.VALIDEE;
    this.#etapeStore.modifier({
      request: {
        etapes: [
          {
            id: etape.id,
            nom: nom!,
            codeEtape: codeEtape,
            duree: duree,
            typesContrat: typesContrat,
            idModalite: etape.modalite?.id,
            aChangementVille: etape.aChangementVille ?? false,
            aCursusInternational: etape.aCursusInternational ?? false,
            estCertifiante: etape.estCertifiante ?? false,
            idCertification: etape.idCertification,
            estOuverte: etape.estOuverte,
            etatValidation: étatValidationÉtape,
            repartitionsAnnee: repartitionsAnnee,
            type: type,
          },
        ],
      },
      next: () =>
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            message:
              étatValidationÉtape === ÉTAT_VALIDATION_ÉTAPE.VALIDEE
                ? "L'étape est bien marquée comme validée et elle est fermée à la modification."
                : "L'étape est invalidée. Vous pouvez maintenant la modifier.",
            titre: `Étape ${étatValidationÉtape === ÉTAT_VALIDATION_ÉTAPE.VALIDEE ? 'validée' : 'invalidée'}`,
            id: toastrId,
          },
          id: toastrId,
        }),
    });
  }

  #toggleDemandeValidation(data: Jalon | Étape | FinParcours) {
    const etape = this.#etapeStore.entityMap()[data.id];

    if (!etape) return;

    if (!this.handleValidationEtape(etape)) return;

    const toastrId = uuidv7();

    const { nom, codeEtape, duree, typesContrat, repartitionsAnnee, type, etatValidation } = etape;

    const étatValidationÉtape =
      etatValidation === ÉTAT_VALIDATION_ÉTAPE.EN_COURS
        ? ÉTAT_VALIDATION_ÉTAPE.A_VALIDER
        : ÉTAT_VALIDATION_ÉTAPE.EN_COURS;
    this.#etapeStore.modifier({
      request: {
        etapes: [
          {
            id: etape.id,
            nom: nom!,
            codeEtape: codeEtape,
            duree: duree,
            typesContrat: typesContrat,
            idModalite: etape.modalite?.id,
            aChangementVille: etape.aChangementVille ?? false,
            aCursusInternational: etape.aCursusInternational ?? false,
            estCertifiante: etape.estCertifiante ?? false,
            idCertification: etape.idCertification,
            estOuverte: etape.estOuverte,
            repartitionsAnnee: repartitionsAnnee,
            type: type,
            etatValidation: étatValidationÉtape,
          },
        ],
      },
      next: () =>
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            message:
              étatValidationÉtape === ÉTAT_VALIDATION_ÉTAPE.A_VALIDER
                ? "L'étape est en attente de validation par un administrateur et fermée à la modification."
                : 'Vous pouvez maintenant la modifier.',
            titre: `La demande de validation est ${étatValidationÉtape === ÉTAT_VALIDATION_ÉTAPE.A_VALIDER ? 'faite' : 'annulée'}`,
            id: toastrId,
          },
          id: toastrId,
        }),
    });
  }

  handleValidationEtape(etape: Étape) {
    const toastrId = uuidv7();

    const {
      nom,
      codeEtape,
      duree,
      typesContrat,
      repartitionsAnnee,
      type,
      estDupliquee,
      certification,
      etatValidation,
    } = etape;

    if (
      (!nom ||
        !codeEtape ||
        !duree ||
        !typesContrat?.length ||
        (!type && !etape.estCertifiante) ||
        (repartitionsAnnee ?? []).filter((x) => x.nombreMois === 0).length > 0 ||
        (typesContrat.includes('apprentissage') && !certification) ||
        !this.isCodeEtapeValid(codeEtape)) &&
      !estDupliquee &&
      etatValidation !== ÉTAT_VALIDATION_ÉTAPE.VALIDEE
    ) {
      toast.custom(ToastrErrorComponent, {
        componentProps: {
          message:
            "Pour valider l'étape, vous devez renseigner les champs marqués comme requis dans les informations de l'étape.",
          id: toastrId,
        },
        id: toastrId,
      });

      this.navigateWithPathParameters(true, undefined, true);
      return false;
    }
    return true;
  }

  #déverrouillerEtape(data: Étape[]) {
    const etapes = this.#etapeStore.entities().filter((x) => data.find((y) => y.id === x.id) && x.estExportee);

    if (!etapes) return;

    const toastrId = uuidv7();

    const req: EtapeBodyRequest[] = etapes.map((x) => ({
      id: x.id,
      nom: x.nom!,
      codeEtape: x.codeEtape,
      duree: x.duree,
      typesContrat: x.typesContrat,
      idModalite: x.modalite?.id,
      aChangementVille: x.aChangementVille ?? false,
      aCursusInternational: x.aCursusInternational ?? false,
      estCertifiante: x.estCertifiante ?? false,
      idCertification: x.idCertification,
      estOuverte: x.estOuverte,
      etatValidation: x.etatValidation,
      repartitionsAnnee: x.repartitionsAnnee,
      type: x.type,
      estExportee: x.estExportee ? false : true,
    }));

    this.#etapeStore.modifier({
      request: {
        etapes: req,
      },
      next: () =>
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            message: (req.length > 1 ? 'Les étapes sont ouvertes' : "L'étape est ouverte") + ' à la modification',
            titre: req.length > 1 ? 'Étapes déverrouillées' : 'Étape déverrouillée',
            id: toastrId,
          },
          id: toastrId,
        }),
    });
  }

  toggleCanvasElementSupprimes() {
    this.estCanvasElementsSupprimes.set(!this.estCanvasElementsSupprimes());
    this.navigateWithPathParameters();
  }

  #fermerEtape(data: Étape) {
    const etape = this.#etapeStore.entityMap()[data.id];

    if (!etape) return;

    this.#etapeStore.modifier({
      request: {
        etapes: [
          {
            id: etape.id,
            nom: etape.nom ?? '',
            codeEtape: etape.codeEtape ?? '',
            duree: etape.duree ?? 0,
            typesContrat: etape.typesContrat ?? [],
            idModalite: etape.modalite?.id ?? null,
            aChangementVille: etape.aChangementVille ?? false,
            aCursusInternational: etape.aCursusInternational ?? false,
            estCertifiante: etape.estCertifiante ?? false,
            estOuverte: !etape.estOuverte,
            repartitionsAnnee: etape.repartitionsAnnee,
            type: etape.type,
            idCertification: etape.certification?.id,
            estModifiee: true,
            etatValidation: etape.etatValidation,
          },
        ],
      },
    });
  }

  #déplacerÉtapes() {
    const idsÉtape = this.#idsEtapesSelectionnees();

    const noeuds = this.fFlow().getState().nodes;

    if (!noeuds) return;

    let etapes: EtapeBodyRequest[] = [];

    idsÉtape.forEach((idÉtape) => {
      const étape = this.noeuds().find((x) => x.id === idÉtape);
      if (!étape) return;

      const noeud = noeuds.find((x) => x.id === idÉtape);
      if (!noeud) return;
      etapes.push({
        ...étape,
        id: idÉtape,
        nom: étape.nom!,
        codeEtape: étape.codeEtape,
        idModalite: étape.modalite?.id,
        metadonnees: { ...étape.metadonnees, x: Math.floor(noeud.position.x), y: Math.floor(noeud.position.y) },
      });
    });
    this.#etapeStore.modifier({
      request: {
        etapes,
      },
    });
  }

  #déplacerGroupeÉtapes() {
    const idGroupe = this.idElementSelectionne();
    const groupe = this.groupesEtapesALaCarte().find((x) => x.id === idGroupe);

    if (!groupe || !idGroupe) return;

    const nodes = this.fFlow().getState().nodes;

    const node = nodes.find((x) => x.id === idGroupe);

    if (!node) return;

    this.#groupeÉtapesStore.modifier({
      idGroupe: idGroupe,
      data: {
        ...groupe,
        idsEtapes: groupe.etapes.map((x) => x.id),
        metadonnees: { ...groupe.metadonnees, x: Math.floor(node.position.x), y: Math.floor(node.position.y) },
      },
    });
  }

  onCanvasContextMenu(event: MouseEvent) {
    const position = this.fFlow().getPositionInFlow({ x: event.x, y: event.y });

    this.#creationCoord = {
      x: position.x,
      y: position.y,
    };
  }

  isCodeEtapeValid(codeEtape?: string) {
    return codeEtape?.match('[A-Z]{3}(0|[3-8])[A-Z]{3}\\d+\\.\\d{1,2}|[A-Z]{3}(0|[3-8])[A-Z]{3}\\d{3}');
  }

  toggleValidationParcours() {
    const livrableRéférentiel = this.#referentielLivrableStore
      .entities()
      .find(
        (x) =>
          x.referentiel.id === this.referentiel()?.id &&
          x.nomLivrable === LivrablePrincipal.PARCOURS_CROISEMENT &&
          x.idParcours === this.parcours()?.id,
      );

    if (!livrableRéférentiel) return;
    let dialogRef;
    if (this.estVerrouille()) {
      dialogRef = this.#dialog.open(ConfirmDialogComponent, {
        context: {
          titre: `Dévalider ${this.parcours()?.nom}`,
          contenu: `Êtes-vous sûr de vouloir dévalider <b>${this.parcours()?.nom}</b> ?
        <br/> Il ne sera plus considéré comme <b>“verrouillé”</b> pour les utilisateurs qui pourront le modifier.`,
          confirmBtnText: 'Dévalider le parcours',
        },
      });
    } else {
      dialogRef = this.#dialog.open(ConfirmDialogComponent, {
        context: {
          titre: `Valider ${this.parcours()?.nom}`,
          contenu: `Êtes-vous sûr de vouloir valider <b>${this.parcours()?.nom}</b> ?
        <br/> Il sera considéré comme <b>“verrouillé”</b> pour les utilisateurs qui ne pourront plus rien le modifier.`,
          messageAlerte: `En tant qu’administrateur, vous pourrez toujours le déverrouiller via le tableau de bord.`,
          confirmBtnText: 'Valider le parcours',
        },
      });
    }
    dialogRef.closed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((result) => {
      if (result) {
        this.#referentielLivrableStore.modifier({
          id: livrableRéférentiel?.id,
          data: {
            etat: livrableRéférentiel.etat,
            estVerrouille: livrableRéférentiel.estVerrouille !== undefined ? !livrableRéférentiel.estVerrouille : true,
          },
        });
      }
    });
  }

  onDoubleClick(id: string) {
    if (!this.#sidepanelService.isOpen()) {
      // On ne recentre le canva sur l'élément que si le sidepanel n'était pas encore ouvert
      // Ca permet de le garder à l'écran
      this.fCanvas().centerGroupOrNode(id.replaceAll('-', ''));
    }
    this.#router.navigate([], { queryParams: { detail: true }, queryParamsHandling: 'merge' });
  }

  #récupérerÉtape(fromCoord: boolean): void {
    this.#sidepanelService.setComponentToDisplay(
      'Récupérer une étape',
      ImportEtapeComponent,
      "Vous pouvez récupérer des étapes d'anciennes cartographies ou d'autres Parcours si besoin.",
      {
        referentiel: this.referentiel(),
        coords: fromCoord ? this.#creationCoord : this.fCanvas().getPosition(),
        parcours: this.parcours()!,
      },
      ImportEtapeFooterComponent,
    );
    this.#sidepanelService.open();
  }
}
